import { Plus, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrapEntryForm } from './ScrapEntryForm';
import { ScrapEntryList } from './ScrapEntryList';
import { fetchScrapEntriesByLine } from '@/core/api';
import { useTenant } from '@/core/hooks/useTenant';
import { useLanguage } from '@/core/LanguageContext';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/utils';

interface LineScrapRowProps {
  entryDate: string;
  factoryId: string;
  areaId: string;
  lineId: string;
  lineName: string;
  areaColor: string;
  isLast?: boolean;
}

/**
 * Compact row for a production line with visual hierarchy indicators.
 */
export const LineScrapRow = ({
  entryDate,
  factoryId,
  areaId,
  lineId,
  lineName,
  areaColor,
  isLast = false,
}: LineScrapRowProps) => {
  const { selectedTenantId } = useTenant();
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const { data: entries = [] } = useQuery({
    queryKey: ['scrap-entries', selectedTenantId, factoryId, entryDate, lineId],
    queryFn: () => fetchScrapEntriesByLine(factoryId, entryDate, lineId),
    enabled: !!factoryId && !!lineId,
  });

  const totalLoss = entries.reduce((sum, entry) => sum + entry.loss_amount, 0);
  const entryCount = entries.length;

  return (
    <div className={cn('relative', !isLast && 'border-b border-border/50')}>
      {/* Row header */}
      <div
        className={cn(
          'flex items-center gap-2 py-2 px-3 hover:bg-muted/30 transition-colors cursor-pointer',
          isExpanded && 'bg-muted/20'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Small colored dot for line */}
        <div 
          className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
          style={{ backgroundColor: areaColor }} 
        />
        
        <span className="text-sm flex-1">{lineName}</span>

        {/* Summary stats - subtle and gray */}
        {entryCount > 0 ? (
          <span className="text-xs text-muted-foreground">
            {entryCount} · €{totalLoss.toFixed(2)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}

        {/* Quick add button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            setShowForm(true);
            setIsExpanded(true);
          }}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>

        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
      </div>

      {/* Expanded content with indentation */}
      {isExpanded && (
        <div className="relative ml-4 pl-4 pb-2 border-l border-border/30">
          {/* Content area */}
          <div className="space-y-2 pt-1">
            <ScrapEntryList factoryId={factoryId} entryDate={entryDate} lineId={lineId} />
            
            {showForm ? (
              <ScrapEntryForm
                entryDate={entryDate}
                areaId={areaId}
                lineId={lineId}
                onSuccess={() => setShowForm(false)}
                onCancel={() => setShowForm(false)}
              />
            ) : (
              <button
                onClick={() => setShowForm(true)}
                className="w-full py-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed rounded hover:border-primary transition-colors"
              >
                + {t('core.scrap.addEntry', 'Add entry')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
